import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Input from '../Input';
import Button from '../Button';
import Dropdown from '../Dropdown';
import Toast from '../Toast';
import CustomizedSwitch from '../Switch';
import FeatureFlagService from '../../services/feature-flag.service';
import TenantService from '../../services/tenant.service';

interface TenantOption {
  id: number;
  tenant_name: string;
}

interface OverrideModalProps {
  open: boolean;
  flagId: number;
  flagCode: string;
  /** Pre-filled and locked when opened from Tenant 360; omitted when opened from the flag detail page (operator picks the tenant). */
  fixedTenantId?: number;
  fixedTenantName?: string;
  onClose: () => void;
  onSaved: () => void;
}

/** US-045 AC-2: Tenant pre-filled from context if from Tenant 360; Flag is always pre-filled (this modal is always opened for one specific flag). */
const OverrideModal: React.FC<OverrideModalProps> = ({
  open,
  flagId,
  flagCode,
  fixedTenantId,
  fixedTenantName,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState<number | ''>(fixedTenantId ?? '');
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [overrideState, setOverrideState] = useState(true);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTenantId(fixedTenantId ?? '');
    setOverrideState(true);
    setReason('');
    setError('');
    if (!fixedTenantId) {
      TenantService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
        setTenants(response?.data?.items ?? []);
      });
    }
  }, [open, fixedTenantId]);

  const handleConfirm = useCallback(async () => {
    if (!tenantId) {
      setError(t('overrideModal.pageTexts.fields.tenant.errors.required'));
      return;
    }
    if (!reason.trim()) {
      setError(t('overrideModal.pageTexts.fields.reason.errors.required'));
      return;
    }
    setSubmitting(true);
    try {
      await FeatureFlagService.setOverride(flagId, { tenantId: Number(tenantId), overrideState, reason });
      toast.success(<Toast message={t('overrideModal.pageTexts.success')} />, { icon: false });
      onSaved();
    } finally {
      setSubmitting(false);
    }
  }, [tenantId, overrideState, reason, flagId, onSaved, t]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('overrideModal.pageTexts.title', { flagCode })}
      content={
        <div className="row g-3">
          <div className="col-12">
            {fixedTenantId ? (
              <Input id="override_tenant" name="override_tenant" label={t('overrideModal.pageTexts.fields.tenant.label')} value={fixedTenantName ?? ''} disabled />
            ) : (
              <Dropdown
                id="override_tenant_select"
                name="override_tenant_select"
                label={t('overrideModal.pageTexts.fields.tenant.label')}
                placeholder={t('overrideModal.pageTexts.fields.tenant.placeholder')}
                value={tenantId}
                error={!!error && !tenantId}
                options={tenants.map((tenant) => ({
                  id: String(tenant.id),
                  name: tenant.tenant_name,
                }))}
                noSearch
                onChange={(e) => setTenantId(e.target.value === '' ? '' : Number(e.target.value))}
              />
            )}
          </div>
          <div className="col-12">
            <CustomizedSwitch
              label={
                overrideState
                  ? t('overrideModal.pageTexts.fields.overrideState.on')
                  : t('overrideModal.pageTexts.fields.overrideState.off')
              }
              name="override_state"
              checked={overrideState}
              onChange={() => setOverrideState((v) => !v)}
            />
          </div>
          <div className="col-12">
            <Input
              id="override_reason"
              name="override_reason"
              label={t('overrideModal.pageTexts.fields.reason.label')}
              required
              value={reason}
              error={!!error && !reason.trim()}
              errorMessage={error}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
      }
      action={
        <>
          <Button className="btn btn-outline-secondary" text={t('featureFlags.form.pageTexts.buttons.cancel')} onClick={onClose} />
          <Button
            className="btn btn-primary"
            text={t('overrideModal.pageTexts.confirm')}
            loading={submitting}
            onClick={handleConfirm}
          />
        </>
      }
    />
  );
};

export default OverrideModal;
